/**
 * The World Market, as far as one district can see it.
 *
 * `DESIGN-GOVERNORS.md` §5 makes it the boundary condition of the whole economy:
 *
 * > The boundary condition standing in for *"the rest of the world."* It posts
 * > prices for goods, compute, fuel, and bond yields. […] The World Market is
 * > bottomless but unsentimental: it always trades, at prices that punish
 * > desperation.
 *
 * Three of those four prices exist here. Two were the two halves of one
 * measurement this build was asked for by name (ADR-024 Q58): **what a district
 * earns**, and **what a debt costs**. The third arrived when something needed to
 * buy something — §7's construction convoys consume goods, so goods have a price
 * and the polity pays it (ADR-029). Fuel waits for a gas plant, which is the same
 * rule applied to the one price still missing: a market posts a price for a thing
 * somebody trades, and inventing one for a flow nothing consumes would be
 * inventing a number to hide a mechanic behind.
 *
 * ## A price is a national input, and nothing here generates one
 *
 * §5 says prices *"follow published slow cycles plus bounded noise […] with
 * occasional regime shifts"*. None of that is in this file, and the omission is
 * deliberate: a schedule is the polity's, the polity is Phase 3's server, and a
 * district that invented its own world prices would be a district that could
 * disagree with the world about them.
 *
 * So a price is a **posted number**, set from outside and carried in the
 * snapshot, exactly as `SurfaceSim.setRainfall` and `setPowerAllocation` already
 * are. What is shipped as a default is §5's *"price of last resort"* — what the
 * market pays a district nobody has quoted.
 *
 * ## What the district actually gets, and what it does not
 *
 * §6's revenue split has four streams and they do not all go to the same place:
 *
 * | Stream | Whose money it becomes | Where it is |
 * |---|---|---|
 * | Compute sales | **the polity's** | here, as a measurement |
 * | Land lease | the district's treasury | `FacilityManager`, since ADR-015 |
 * | Wages | staff families' cash | `LabourManager`, since ADR-016 |
 * | Local procurement | **host-district corporations' revenue** | here |
 *
 * And one stream that runs the other way: **goods**, bought from the world and
 * delivered by lorry into a building site (`buyGoods`). Nothing local is bought
 * and nobody local is paid — §6's procurement is a share of *opex*, and a slab
 * that is not running has none. A district hosting construction gets the traffic
 * and none of the trade.
 *
 * The first line is the one that matters for §8's *"facility profits dwarf tithe
 * income"*, and the district never sees a cent of it. It is booked here anyway,
 * because the polity treasury that will claim it does not exist yet and Law 5
 * wants the measurement to predate the case — the sixth ledger in this build to
 * ship ahead of the mechanic that reads it, after `deliveredKwCycles`,
 * `uptimeCycles`, `wagesPaid`, `worksSpending` and `powerBilled`.
 *
 * The last line is the one that changes the game. §6 gives it one sentence —
 * *"1–2 host-district corporations get standing orders […] Fraction of opex"* —
 * and it is the only channel in the design through which federal money reaches a
 * district's **tax base** rather than its rent roll. It is paid as ordinary
 * corporate revenue into ordinary corporations, so the district's cut of it is
 * whatever `TAX_RATE` is, arriving through machinery that predates every word of
 * this design. Nothing here knows what a tax is.
 *
 * ## Every number here is counted
 *
 * Law 1: sales are units produced times a posted price, opex is a rate times an
 * uptime, procurement is a share of opex. The units come from the estate's own
 * `min(power, water, staffing)` and this class cannot see any of the three. A
 * district with no facility on it sells nothing, spends nothing, procures
 * nothing, and hashes as though this class were not here.
 *
 * ## What this class cannot see
 *
 * A hash type, and nothing else. Not the estate, not the treasury, not a
 * corporation, not the ground. It is handed a cycle's production and hands back
 * dollars; who produced it and who receives them are both somebody else's
 * business, and `tests/purity.test.ts` pins the import list to keep it that way.
 *
 * See `docs/world-market.md`.
 */

import { Canonical } from '../hashState';
import { CYCLES_PER_SEASON } from '../SimClock';

/**
 * The prices of last resort, and the share of opex that is spent locally.
 *
 * Two of these are prices the world posts and two are structure. The prices are
 * *state* as well — a governor may post a different one — and what lives here is
 * only what a district is quoted when nobody has quoted it anything.
 */
export interface MarketConfig {
  /**
   * Dollars per compute-unit, unquoted.
   *
   * §5 gives compute *"world market (volatile boom demand)"* and no number, so
   * this is chosen, and what it is chosen against is written down in
   * `docs/world-market.md`: at the estate's shipped capacity a data centre at
   * full uptime sells about **$216,000 a season** and spends about $144,000
   * running, which is a third of margin — a plausible figure for the business,
   * and one that makes §8's *"facility profits dwarf tithe income"* arithmetic
   * rather than a claim. A 22% tithe on this district's tax base is $3,200; the
   * profit on one data centre is twenty-two times that.
   */
  COMPUTE_PRICE: number;
  /**
   * Dollars per goods-unit, unquoted.
   *
   * §5's third posted price, and the first of the four whose *buyer* is the
   * polity rather than a seller of anything: goods are what construction consumes
   * (*"Construction (all building consumes goods)"*), and every unit of them that
   * moves is a lorry on somebody's map.
   *
   * $300 is chosen against one figure and it is the facility's own: a data centre
   * takes 240 goods to build (`DATA_CENTER_GOODS` over `DATA_CENTER_BUILD_SEASONS`)
   * and makes about $72,000 of operating profit in a season at full uptime
   * (`docs/world-market.md`), so **a data centre costs about one season of its own
   * margin to build**. That is the shape §8 wants — capex the polity notices,
   * against an asset that pays it back inside a year — and it is what makes §7's
   * *"higher capex"* for a forced siting a sentence with somewhere to land when
   * the Siting Protocol arrives to charge it.
   *
   * There is no goods *market* here: a district neither produces goods nor holds
   * a stock of them. This is a price the polity pays the world for what it ships
   * in, and the only thing in this build that reads it is a convoy.
   */
  GOODS_PRICE: number;
  /**
   * What the district pays on its debt, per budget cycle, unquoted.
   *
   * §8: debt interest *"stops being a local slider and becomes a World Market
   * rate"*. This is that rate, and it is the number ADR-024 D160 measured and
   * declined to set:
   *
   * > At **0.0005 a cycle** — 4.6% a season, a plausible sovereign coupon — the
   * > debt grows roughly linearly rather than hyperbolically, the pool after six
   * > seasons is a number a person can read, and the tithe is audible against it.
   *
   * The old 0.02 was 494% a season. A district in debt sextupled it every season
   * and the Chronicle §16 publishes went into scientific notation by season six.
   * Nothing about that was a difficulty setting; it was a rate that had never
   * been asked to survive ninety compoundings, because until ADR-012 there was no
   * season for it to survive.
   *
   * §8's *"published deterministic function of debt-to-revenue"* is the polity's
   * bond yield and is not this: this is what one district borrows at, and there
   * is nobody above it yet to price the difference.
   */
  BOND_YIELD: number;
  /**
   * The share of a facility's operating spend that is placed with host-district
   * corporations. §6's *"Fraction of opex"*.
   *
   * The one number in the split that decides whether hosting the estate is worth
   * anything to the district's own economy, because it is the only one that
   * reaches the tax base. Lease is rent and wages are households; this is
   * turnover.
   */
  PROCUREMENT_RATE: number;
  /**
   * How many corporations hold the standing orders. §6's *"1–2"*.
   *
   * A count rather than a list, because which ones is geography (`SurfaceSim`
   * picks the built corporations nearest the facility's gate) and geography is
   * the mayor's business. A district that sites its shops next to the slab
   * catches the orders; one that does not, does not.
   */
  PROCUREMENT_SUPPLIERS: number;
}

export const DEFAULT_MARKET_CONFIG: MarketConfig = {
  COMPUTE_PRICE: 60,
  GOODS_PRICE: 300,
  BOND_YIELD: 0.0005,
  PROCUREMENT_RATE: 0.3,
  PROCUREMENT_SUPPLIERS: 2,
};

/** What one budget cycle of the estate's operation moved. */
export interface CycleTrade {
  /** Compute-units produced and sold. `uptime × capacity`. */
  computeUnits: number;
  /** What they fetched at the posted price. The polity's revenue. */
  salesRevenue: number;
  /** What running the estate cost. The polity's spending. */
  operatingCost: number;
  /** The share of that spend owed to host-district corporations. */
  procurement: number;
}

/** The market's complete state. See `MarketManager.save()`. */
export interface MarketSnapshot {
  computePrice: number;
  goodsPrice: number;
  bondYield: number;
  computeSold: number;
  salesRevenue: number;
  operatingCost: number;
  procurementPaid: number;
  goodsBought: number;
  capitalSpend: number;
  tradedCycles: number;
  postings: number;
}

/**
 * The district's window onto the world market.
 *
 * Owned by `SurfaceSim`, settled once per budget cycle alongside the two flows
 * and the estate, and snapshotted inside the surface like every other manager
 * here. Holds no `Rng`: a posted price is somebody's decision and a sale is
 * arithmetic, so there is nothing to roll.
 */
export class MarketManager {
  private config: MarketConfig;

  // --- The posted prices ----------------------------------------------------
  //
  // State rather than configuration, because a governor moves them and a
  // snapshot has to remember where they were left. They open at the config's
  // price of last resort, which is what an unquoted district trades at.

  private computePrice: number;
  private goodsPrice: number;
  private bondYield: number;

  // --- The ledger -----------------------------------------------------------

  private computeSold = 0;
  private salesRevenue = 0;
  private operatingCost = 0;
  private procurementPaid = 0;
  /** Goods-units the polity has bought for construction here. Also lorries. */
  private goodsBought = 0;
  /**
   * Dollars it has spent doing so. §8's *"Out: facility construction"*.
   *
   * Kept apart from `operatingCost` and not netted into `getOperatingProfit`,
   * because they are different kinds of money: opex is what a running machine
   * costs and capex is what a machine cost to have. A reader that wants the
   * polity's whole position here subtracts both, and §16's Chronicle publishes
   * the three sums rather than the difference.
   */
  private capitalSpend = 0;
  /** Budget cycles in which the estate produced or spent anything. */
  private tradedCycles = 0;
  /** Times a posted price actually moved. What a Chronicle would cite. */
  private postings = 0;

  constructor(config: Partial<MarketConfig> = {}) {
    this.config = { ...DEFAULT_MARKET_CONFIG, ...config };
    this.computePrice = this.config.COMPUTE_PRICE;
    this.goodsPrice = this.config.GOODS_PRICE;
    this.bondYield = this.config.BOND_YIELD;
  }

  getConfig(): MarketConfig {
    return { ...this.config };
  }

  /**
   * Change the world parameters.
   *
   * The posted prices follow **only if nobody has posted one**. A world whose
   * price of last resort is edited under a district that has been quoted a
   * different figure keeps the quote, because the quote is the more specific
   * fact and a settings panel is not a governor.
   */
  updateConfig(partial: Partial<MarketConfig>): void {
    const before = this.config;
    this.config = { ...this.config, ...partial };
    if (this.postings > 0) return;
    if (before.COMPUTE_PRICE !== this.config.COMPUTE_PRICE) this.computePrice = this.config.COMPUTE_PRICE;
    if (before.GOODS_PRICE !== this.config.GOODS_PRICE) this.goodsPrice = this.config.GOODS_PRICE;
    if (before.BOND_YIELD !== this.config.BOND_YIELD) this.bondYield = this.config.BOND_YIELD;
  }

  /**
   * Whether the world has ever done anything to this district.
   *
   * True while no price has moved and nothing has traded — which is every
   * district in this repository's golden masters, and the reason a market could
   * arrive without re-recording a single one of them. The guard every peer in
   * this package has (`FiscalManager.empty`, `SeasonFacilities`): an untouched
   * market and no market are the same market.
   */
  get empty(): boolean {
    return (
      this.postings === 0 &&
      this.tradedCycles === 0 &&
      this.procurementPaid === 0 &&
      this.goodsBought === 0
    );
  }

  // --- The prices -----------------------------------------------------------

  /** Dollars per compute-unit, as posted to this district. */
  getComputePrice(): number {
    return this.computePrice;
  }

  /** Dollars per goods-unit, as posted. What a convoy's cargo cost. */
  getGoodsPrice(): number {
    return this.goodsPrice;
  }

  /** What the district pays on debt, per budget cycle, as posted. */
  getBondYield(): number {
    return this.bondYield;
  }

  /**
   * Post a price. Returns whether anything moved.
   *
   * A national input, in the same standing as `setRainfall`: nothing inside the
   * simulation calls it, and whatever does is standing in for a world that does
   * not exist yet. Both fields are optional and an omitted one is left alone —
   * a market that reprices compute has not repriced debt.
   *
   * Non-finite and negative figures are refused rather than clamped, for
   * `FiscalManager.setTitheRate`'s reason: §16 publishes what was posted, and a
   * record that quietly reported a rate nobody set would be wrong about the one
   * thing it exists to be right about.
   */
  post(prices: { computePrice?: number; goodsPrice?: number; bondYield?: number }): boolean {
    let moved = false;
    const { computePrice, goodsPrice, bondYield } = prices;

    if (computePrice !== undefined && Number.isFinite(computePrice) && computePrice >= 0 &&
        computePrice !== this.computePrice) {
      this.computePrice = computePrice;
      moved = true;
    }
    if (goodsPrice !== undefined && Number.isFinite(goodsPrice) && goodsPrice >= 0 &&
        goodsPrice !== this.goodsPrice) {
      this.goodsPrice = goodsPrice;
      moved = true;
    }
    if (bondYield !== undefined && Number.isFinite(bondYield) && bondYield >= 0 &&
        bondYield !== this.bondYield) {
      this.bondYield = bondYield;
      moved = true;
    }

    if (moved) this.postings++;
    return moved;
  }

  // --- The trade ------------------------------------------------------------

  /**
   * Sell one budget cycle's production, and book what running it cost.
   *
   * §5's market *"always trades"*, so there is no clearing, no inventory and no
   * unsold surplus: whatever the estate made this cycle is sold this cycle at
   * the posted price. That is the boundary condition doing its job — a district
   * is a price-taker and the world is bottomless — and it is why nothing here
   * can fail.
   *
   * The two arguments come from the estate's meter and are already multiplied by
   * `min(power, water, staffing)`, which is §4's rule. **A curtailed data centre
   * therefore buys less locally**, and that is the whole reason opex is metered
   * rather than fixed: a dark machine hall orders no parts and buys nobody lunch,
   * so the governor's water priority list reaches the host district's shopkeepers
   * without passing through anything that knows they exist.
   *
   * Returns the cycle's trade, procurement included. This class does not pay it:
   * it does not know what a corporation is.
   */
  settleCycle(computeUnits: number, operatingCost: number): CycleTrade {
    const units = Math.max(0, computeUnits);
    const opex = Math.max(0, operatingCost);
    const revenue = units * this.computePrice;
    const procurement = opex * Math.max(0, this.config.PROCUREMENT_RATE);

    if (units === 0 && opex === 0) {
      return { computeUnits: 0, salesRevenue: 0, operatingCost: 0, procurement: 0 };
    }

    this.computeSold += units;
    this.salesRevenue += revenue;
    this.operatingCost += opex;
    this.tradedCycles++;

    return { computeUnits: units, salesRevenue: revenue, operatingCost: opex, procurement };
  }

  /**
   * Buy goods for a building site, at the posted price. Returns what they cost.
   *
   * §5's market *"always trades"* in this direction too, so there is no shortage,
   * no queue and no allocation: the polity is a price-taker and the world is
   * bottomless, exactly as it is when it sells compute into it.
   *
   * **Bought when the lorry sets off, not when it arrives.** A convoy that gives
   * up in the district's traffic is a convoy the polity paid for and nobody
   * received — which is the arrow §5 draws when it says freight capacity is
   * *"routes × road quality × congestion"*, pointed for once at the polity's
   * account rather than at the district's. A mayor who lets their roads rot makes
   * somebody else's building site more expensive, and nothing anywhere tells
   * either of them.
   *
   * This class does not know what a facility is and does not learn here: it is
   * handed a number of goods-units and hands back dollars.
   */
  buyGoods(units: number): number {
    const goods = Math.max(0, units);
    if (goods === 0) return 0;
    const cost = goods * this.goodsPrice;
    this.goodsBought += goods;
    this.capitalSpend += cost;
    return cost;
  }

  /** What the polity has spent building here, lifetime. §8's construction line. */
  getCapitalSpend(): number {
    return this.capitalSpend;
  }

  /**
   * Book procurement that has actually reached a corporation.
   *
   * The same split `FacilityManager.recordLease` makes, for the same reason and
   * with one more tooth in it. `settleCycle` returns what the estate *owes* the
   * local economy; this records what the local economy *took*. A district with a
   * facility and no built corporation is offered the orders and has nobody to
   * fill them, and the two figures differ — so `procurementPaid` means what
   * `SeasonMarket` says it means, *"dollars placed with host-district
   * corporations"*, rather than dollars a rate implied.
   *
   * The difference is not recorded anywhere and is not money that went missing:
   * it left an account that does not exist yet. When the polity treasury arrives
   * it will be short by exactly this, and that is the honest state of a company
   * town with no town.
   */
  recordProcurement(amount: number): void {
    if (!(amount > 0)) return;
    this.procurementPaid += amount;
  }

  /**
   * What the estate has earned the polity, lifetime. Sales less what it cost.
   *
   * §8's polity treasury line — *"In: facility operating profits (goods/compute
   * sales)"* — with nowhere to be paid into. Negative is a real state and is not
   * clamped: a facility curtailed below the point where its sales cover its
   * running costs is a facility the polity is subsidising, which is a fact §7's
   * contracts should be able to argue about.
   */
  getOperatingProfit(): number {
    return this.salesRevenue - this.operatingCost;
  }

  /** The lifetime totals. Monotone sums, so a season's trade is a subtraction. */
  getLedger() {
    return {
      computeSold: this.computeSold,
      salesRevenue: this.salesRevenue,
      operatingCost: this.operatingCost,
      procurementPaid: this.procurementPaid,
      goodsBought: this.goodsBought,
      capitalSpend: this.capitalSpend,
      tradedCycles: this.tradedCycles,
      postings: this.postings,
    };
  }

  /**
   * What one season of uninterrupted operation would fetch, at today's price.
   *
   * A forecast, and the only figure in this file that is not a sum over
   * something that happened. It exists for §12's Ledger Dock and for a mayor
   * deciding whether to court a facility, and it is deliberately not in the
   * `SeasonRecord`: a Chronicle is a record of what occurred, and this is a
   * multiplication over what did not.
   */
  quoteSeason(computeCapacityPerCycle: number): number {
    return Math.max(0, computeCapacityPerCycle) * this.computePrice * CYCLES_PER_SEASON;
  }

  // --- State ---------------------------------------------------------------

  save(): MarketSnapshot {
    return {
      computePrice: this.computePrice,
      goodsPrice: this.goodsPrice,
      bondYield: this.bondYield,
      computeSold: this.computeSold,
      salesRevenue: this.salesRevenue,
      operatingCost: this.operatingCost,
      procurementPaid: this.procurementPaid,
      goodsBought: this.goodsBought,
      capitalSpend: this.capitalSpend,
      tradedCycles: this.tradedCycles,
      postings: this.postings,
    };
  }

  /**
   * Restore from `save()`.
   *
   * The two prices matter here the way `FiscalManager`'s three marks matter
   * there: a district restored at the price of last resort when it had been
   * quoted twice that sells the same compute for half the money, silently, and
   * the only place it would show is a Chronicle line nobody is comparing.
   */
  load(state: MarketSnapshot): void {
    this.computePrice = state.computePrice;
    this.goodsPrice = state.goodsPrice;
    this.bondYield = state.bondYield;
    this.computeSold = state.computeSold;
    this.salesRevenue = state.salesRevenue;
    this.operatingCost = state.operatingCost;
    this.procurementPaid = state.procurementPaid;
    this.goodsBought = state.goodsBought;
    this.capitalSpend = state.capitalSpend;
    this.tradedCycles = state.tradedCycles;
    this.postings = state.postings;
  }

  /**
   * Order-stable view of the market, for hashing — and nothing while it is empty.
   *
   * The posted prices are in it as well as the totals, and they have to be: two
   * districts that agree on every unit ever sold and disagree about what the next
   * one fetches have not diverged *yet*, and will at the next cycle.
   */
  canonicalState(): Canonical | undefined {
    if (this.empty) return undefined;
    return {
      computePrice: this.computePrice,
      goodsPrice: this.goodsPrice,
      bondYield: this.bondYield,
      computeSold: this.computeSold,
      salesRevenue: this.salesRevenue,
      operatingCost: this.operatingCost,
      procurementPaid: this.procurementPaid,
      goodsBought: this.goodsBought,
      capitalSpend: this.capitalSpend,
      tradedCycles: this.tradedCycles,
      postings: this.postings,
    };
  }
}
